import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, catchError, map, of, switchMap, take, tap, throwError } from 'rxjs';
import { Classroom, ClassroomStatus } from './classroom-shared.types';
import { environment } from 'src/environments/environment';
import { ApiResponse } from '../utils/api-response';
import { ClassroomWithStockRequests } from 'src/app/modules/app/classroom/classroom.types';

@Injectable({ providedIn: 'root' })
export class ClassroomSharedService {
    // Base URLs
    private readonly BASE_URL = environment.apiUrl;
    private readonly CLASSROOMS_URL = `${this.BASE_URL}/classrooms`;

    // BehaviorSubjects
    private _classroom: BehaviorSubject<Classroom | null> = new BehaviorSubject<Classroom | null>(null);
    private _classrooms: BehaviorSubject<Classroom[] | null> = new BehaviorSubject<Classroom[] | null>(null);

    constructor(private _httpClient: HttpClient) { }

    // Accessors
    get classroom$(): Observable<Classroom | null> {
        return this._classroom.asObservable();
    }

    get classrooms$(): Observable<Classroom[] | null> {
        return this._classrooms.asObservable();
    }

    // Public Methods
    getClassrooms(schoolCode: string): Observable<ApiResponse<Classroom[]>> {
        return this._httpClient
            .get<ApiResponse<Classroom[]>>(`${this.CLASSROOMS_URL}/school/${schoolCode}`)
            .pipe(
                tap((response) => {
                    if (response.success && response.data) {
                        this._classrooms.next(response.data);
                    }
                }),
                catchError((error) => {
                    console.error('Failed to fetch classrooms:', error);
                    const errorResponse: ApiResponse<Classroom[]> = error?.error || {
                        success: false,
                        message: error.message || 'Failed to fetch classrooms',
                        data: null,
                        timestamp: new Date()
                    };
                    return throwError(() => errorResponse);
                })
            );
    }

    getStaffClassrooms(
        schoolCode: string,
        staffCode: string,
        search: string='',
        page: number=0,
        size: number= 100,
        sortField: string = '',
        sortOrder: number = 1,
        filters: { [key: string]: any } = {}): Observable<ApiResponse<Classroom[]>> {
        
        let url = `${this.CLASSROOMS_URL}/school/${schoolCode}/paged/staff/${staffCode}?page=${page}&size=${size}`;
        if (search) url += `&search=${encodeURIComponent(search)}`;
        if (sortField) url += `&sort=${sortField},${sortOrder === 1 ? 'asc' : 'desc'}`;

        // Add filters as query params (array or object)
        Object.keys(filters).forEach(key => {
            const value = filters[key];
            if (Array.isArray(value)) {
                value.forEach(v => url += `&${key}=${encodeURIComponent(v)}`);
            } else if (value !== undefined && value !== null && value !== '') {
                url += `&${key}=${encodeURIComponent(value)}`;
            }
        });

        return this._httpClient
            .get<ApiResponse<Classroom[]>>(url)
            .pipe(
                tap((response) => {
                    if (response.success && response.data) {
                        this._classrooms.next(response.data);
                    }
                }),
                catchError((error) => {
                    console.error('Failed to fetch classrooms:', error);
                    const errorResponse: ApiResponse<Classroom[]> = error?.error || {
                        success: false,
                        message: error.message || 'Failed to fetch classrooms',
                        data: null,
                        timestamp: new Date()
                    };
                    return throwError(() => errorResponse);
                })
            );
    }

    
    getClassroomCounts(): Observable<ApiResponse<{ total: number; active: number; inactive: number }>> {
        return this.classrooms$.pipe(
            take(1),
            map(classrooms => {
                const counts = {
                    total: classrooms?.length || 0,
                    active: classrooms?.filter(c => c.status === ClassroomStatus.ACTIVE).length || 0,
                    inactive: classrooms?.filter(c => c.status === ClassroomStatus.INACTIVE).length || 0
                };
                return {
                    success: true,
                    data: counts,
                    message: 'Classroom counts retrieved successfully',
                    timestamp: new Date()
                };
            })
        );
    }




    searchClassrooms(
        schoolCode: string,
        search: string = '',
        page: number = 0,
        size: number = 5
      ): Observable<ApiResponse<{ content: Classroom[]; totalElements: number }>> {
        let params = new HttpParams()
          .set('page', page.toString())
          .set('size', size.toString());
      
        if (search.trim()) {
          params = params.set('search', search.trim());
        }
      
        return this._httpClient
          .get<ApiResponse<{ content: Classroom[]; totalElements: number }>>(
            `${this.CLASSROOMS_URL}/school/${schoolCode}/paged`,
            { params }
          )
          .pipe(
            catchError((error) => {
              console.error('Search failed:', error);
              const errorResponse: ApiResponse<Classroom[]> = error?.error || {
                success: false,
                message: error.message || 'Search failed',
                data: null,
                timestamp: new Date()
              };
              return throwError(() => errorResponse);
            })
          );
      }

        getClassroomsPagedForStaff(
              schoolCode: string,
              staffCode: string,
              search: string,
              page: number,
              size: number,
              sortField: string = '',
              sortOrder: number = 1,
              filters: { [key: string]: any } = {}
          ): Observable<ApiResponse<{ content: ClassroomWithStockRequests[]; totalElements: number }>> {
              let url = `${this.BASE_URL}/classrooms/school/${schoolCode}/paged/staff/${staffCode}?search=${encodeURIComponent(search)}&page=${page}&size=${size}`;
              if (sortField) url += `&sort=${sortField},${sortOrder === 1 ? 'asc' : 'desc'}`;
      
              Object.keys(filters).forEach(key => {
                  const value = filters[key];
                  if (Array.isArray(value)) {
                      value.forEach(v => url += `&${key}=${encodeURIComponent(v)}`);
                  } else if (value !== undefined && value !== null && value !== '') {
                      url += `&${key}=${encodeURIComponent(value)}`;
                  }
              });
      
              return this._httpClient.get<ApiResponse<{ content: ClassroomWithStockRequests[]; totalElements: number }>>(url)
                  .pipe(
                      tap((response) => {
                        //   if (response.success && response.data && Array.isArray(response.data.content)) {
                        //       this._classroomsWithRequests.next(response.data.content);
                        //   }
                      }),
                      catchError((error) => {
                          return throwError(() => error);
                      })
                  );
          }
}
